import { useRef } from 'react'

/**
 * Custom hook for keyboard navigation in radiogroup components
 * @param {Array} items - Array of items to navigate
 * @param {*} selectedValue - Currently selected value
 * @param {Function} setSelectedValue - Function to update selected value
 * @param {Function} filterFn - Optional filter function to determine enabled items
 * @param {Object} options - Optional configuration
 * @param {string} options.buttonSelector - CSS selector for buttons (default: 'button[role="radio"]')
 * @param {Function} options.getValueFromItem - Function to extract value from item (default: item => item.id)
 */
export default function useKeyboardNavigation(
  items,
  selectedValue,
  setSelectedValue,
  filterFn = null,
  options = {}
) {
  const {
    buttonSelector = 'button[role="radio"]',
    getValueFromItem = (item) => item.id,
  } = options

  const radiogroupRef = useRef(null)

  // Helper: get enabled buttons inside the radiogroup
  const getEnabledButtons = () => {
    const root = radiogroupRef.current
    if (!root) return []
    return Array.from(root.querySelectorAll(buttonSelector))
  }

  const onRadioKeyDown = (e, idx) => {
    const enabledItems = filterFn ? items.filter(filterFn) : items
    if (enabledItems.length === 0) return

    const currentIndex = enabledItems.findIndex(
      (item) => getValueFromItem(item).toString() === selectedValue?.toString()
    )

    const moveFocus = (nextIdx) => {
      const nextItem = enabledItems[nextIdx]
      if (!nextItem) return
      const nextValue = getValueFromItem(nextItem)
      setSelectedValue(nextValue)
      const btns = getEnabledButtons()
      if (btns[nextIdx]) btns[nextIdx].focus()
    }

    const handleActivation = (event, idx) => {
      const btns = getEnabledButtons()
      const pressedIdx = event.currentTarget
        ? btns.indexOf(event.currentTarget)
        : -1
      const safeIdx = pressedIdx >= 0 ? pressedIdx : idx
      const item = enabledItems[safeIdx]
      if (item) {
        const value = getValueFromItem(item)
        // If filterFn is provided, check if item passes filter
        if (!filterFn || filterFn(item)) {
          setSelectedValue(value)
        }
      }
    }

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        e.preventDefault()
        const next =
          currentIndex < 0
            ? 0
            : Math.min(currentIndex + 1, enabledItems.length - 1)
        moveFocus(next)
        break
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        e.preventDefault()
        const prev = currentIndex < 0 ? 0 : Math.max(currentIndex - 1, 0)
        moveFocus(prev)
        break
      }
      case ' ':
      case 'Enter': {
        e.preventDefault()
        handleActivation(e, idx)
        break
      }
      default:
        // Optional: allow Space via e.code for readability (not needed in modern browsers)
        if (e.code === 'Space') {
          e.preventDefault()
          handleActivation(e, idx)
        }
        break
    }
  }

  return { onRadioKeyDown, radiogroupRef }
}
